import { peekQueryClient } from "@/queries/client";
import { scopeFor, ScopeNotReadyError } from "@/queries/scope";
import { TAB_ID } from "@/lib/tab-id";
import { HINT_TARGETS } from "./targets";
import type { TCached, THintTargets } from "./types";

/**
 * Cache-invalidation hints pushed over the workspace's notification stream
 * (NG-2666), so another user's write reaches this tab in seconds rather than
 * when an entry's freshness runs out.
 *
 * A hint names a domain and a scope, never data: `{type: "cache.invalidate",
 * workspace_id, domain, scope}`, plus any field the service adds (it adds
 * `channel`), which is ignored. A record hint also names the records that
 * changed, `ids` (NG-3588), which its row gets. This tab marks the matching cached reads stale
 * and re-reads them through the normal API, so permissions stay on the read
 * path. The expiry times stay as the backstop for any writer that sends no
 * hint.
 *
 * A hint caused by a write from a browser tab names that tab, `origin_tab_id`
 * (NG-3587, `lib/tab-id.ts`). When it is this tab, a row that sets
 * `skipOwnReread` is marked but not re-read (`types.ts`).
 *
 * What each hint does is a row in `cache-hints/<domain>.ts` (see
 * `targets.ts`). The listener owns the rest: parsing, the workspace check,
 * the jitter, one re-read per burst and the hidden-tab wait. A row never sets
 * a timer or reads `document`.
 */
const CACHE_INVALIDATE_EVENT = "cache.invalidate";

/**
 * The longest a hint's re-read waits. Every open tab in the workspace hears the
 * same hint at the same moment; a random delay up to this spreads their reads.
 */
const HINT_REREAD_JITTER_MS = 2_000;

interface TCacheHint {
  readonly workspace_id: string;
  readonly domain: string;
  readonly scope: string;
  /** A record hint's records; absent from a list hint. */
  readonly ids?: readonly string[];
  /**
   * The tab whose write caused the hint; absent when no tab's did. Only ever
   * compared with this tab's id, so a value of any other shape names no tab.
   */
  readonly origin_tab_id?: unknown;
}

/**
 * The parts of the root store the listener and the rows use. Spelled out
 * rather than imported, because the root store registers the listener and an
 * import back would be a cycle.
 */
interface TCacheHintsHost {
  readonly notification: {
    addStreamListener(listener: {
      readonly event: string;
      onMessage(workspaceSlug: string, data: string): void;
      onReopen?(workspaceSlug: string): void;
    }): () => void;
  };
  readonly workspace: { findBySlug(slug: string): { readonly id: string } | undefined };
}

/**
 * Start reading hints off this tab's notification streams; returns the
 * function that stops it. `targets` is every domain's rows; a test passes its
 * own.
 */
export function listenForCacheHints(
  root: TCacheHintsHost,
  targets: THintTargets<TCacheHintsHost> = HINT_TARGETS,
): () => void {
  const rereads = scheduleRereads(root, targets);
  const stopListening = root.notification.addStreamListener({
    event: CACHE_INVALIDATE_EVENT,
    onMessage(workspaceSlug, data) {
      const hint = parseHint(workspaceSlug, data);
      if (!hint) return;
      // The service already files streams by workspace; a hint for another
      // workspace is ignored here as well.
      if (hint.workspace_id !== root.workspace.findBySlug(workspaceSlug)?.id) return;
      const targetKey = `${hint.domain}:${hint.scope}`;
      const target = targets[targetKey];
      if (!target) return;
      const cached = cachedScope(workspaceSlug);
      if (!cached) return;
      const ids = hint.ids ? new Set(hint.ids) : undefined;
      target.mark(root, workspaceSlug, cached, ids);
      if (target.skipOwnReread && hint.origin_tab_id === TAB_ID) return;
      rereads.request(workspaceSlug, targetKey, ids);
    },
    onReopen(workspaceSlug) {
      const cached = cachedScope(workspaceSlug);
      if (!cached) return;
      // Pub/sub keeps nothing for a dropped stream, so any hint sent while it
      // was down is lost: mark everything this principal holds in the
      // workspace stale, and the next read of each asks again.
      void cached.client.invalidateQueries({
        queryKey: [cached.scope.tenant, cached.scope.principal],
      });
    },
  });
  return () => {
    stopListening();
    rereads.stop();
  };
}

const isHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";

/**
 * When each hint's `reread` runs. One pending re-read per workspace and
 * target, so a burst is one read, of every record the burst named. A re-read requested or due while the tab is
 * hidden waits for the `visibilitychange` that shows it again, then takes its
 * own random delay like any other. The document listener is attached only
 * while something waits, so a tab that never gets a hint while hidden never
 * listens.
 */
function scheduleRereads(root: TCacheHintsHost, targets: THintTargets<TCacheHintsHost>) {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const waitingForVisible = new Map<string, () => void>();
  // The records each pending re-read names so far; `undefined` once any hint
  // in the burst named none.
  const namedIds = new Map<string, Set<string> | undefined>();

  const waitForVisible = (key: string, run: () => void) => {
    if (waitingForVisible.size === 0 && typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }
    waitingForVisible.set(key, run);
  };

  const schedule = (key: string, run: () => void) => {
    if (timers.has(key) || waitingForVisible.has(key)) return;
    if (isHidden()) {
      waitForVisible(key, run);
      return;
    }
    const timer = setTimeout(() => {
      timers.delete(key);
      if (isHidden()) {
        waitForVisible(key, run);
        return;
      }
      run();
    }, Math.random() * HINT_REREAD_JITTER_MS);
    timers.set(key, timer);
  };

  function onVisibilityChange() {
    if (isHidden()) return;
    document.removeEventListener("visibilitychange", onVisibilityChange);
    const due = [...waitingForVisible];
    waitingForVisible.clear();
    for (const [key, run] of due) schedule(key, run);
  }

  return {
    request(workspaceSlug: string, targetKey: string, ids?: ReadonlySet<string>) {
      const key = `${workspaceSlug}|${targetKey}`;
      if (!namedIds.has(key)) {
        namedIds.set(key, ids ? new Set(ids) : undefined);
      } else {
        const named = namedIds.get(key);
        if (!named || !ids) namedIds.set(key, undefined);
        else for (const id of ids) named.add(id);
      }
      schedule(key, () => {
        const named = namedIds.get(key);
        namedIds.delete(key);
        // Asked again at run time: the session can change while a re-read
        // waits.
        const target = targets[targetKey];
        if (!target) return;
        const cached = cachedScope(workspaceSlug);
        if (!cached) return;
        target.reread(root, workspaceSlug, cached, named);
      });
    },
    stop() {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
      waitingForVisible.clear();
      namedIds.clear();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    },
  };
}

function parseHint(workspaceSlug: string, data: string): TCacheHint | null {
  let payload: unknown;
  try {
    payload = JSON.parse(data);
  } catch (error) {
    console.warn("[cache-hints] unreadable hint ignored", { workspaceSlug, data }, error);
    return null;
  }
  if (
    typeof payload !== "object" ||
    payload === null ||
    typeof (payload as TCacheHint).workspace_id !== "string" ||
    typeof (payload as TCacheHint).domain !== "string" ||
    typeof (payload as TCacheHint).scope !== "string"
  ) {
    console.warn("[cache-hints] malformed hint ignored", { workspaceSlug, data });
    return null;
  }
  const { ids, ...hint } = payload as TCacheHint & { ids?: unknown };
  if (ids === undefined) return hint;
  if (Array.isArray(ids) && ids.length > 0 && ids.every((id) => typeof id === "string")) {
    return { ...hint, ids };
  }
  // Read as naming no records, so a row covering records re-reads every one
  // rather than none. An empty list counts: the backend sends no hint at all
  // when no record changed, so an empty one would otherwise re-read nothing.
  console.warn("[cache-hints] malformed ids read as every record", { workspaceSlug, data });
  return hint;
}

/**
 * The client and scope to invalidate under, or null when there is nothing
 * cached to reach: no client yet, or no signed-in principal to key under.
 */
function cachedScope(workspaceSlug: string): TCached | null {
  const client = peekQueryClient();
  if (!client) return null;
  try {
    return { client, scope: scopeFor(workspaceSlug) };
  } catch (error) {
    if (!(error instanceof ScopeNotReadyError)) throw error;
    // Nothing is cached before the session resolves, so nothing is stale.
    console.warn("[cache-hints] hint before the session resolved; nothing to invalidate", {
      workspaceSlug,
    });
    return null;
  }
}
